import React, { useState, useEffect, useCallback } from 'react';
import { 
  HelpCircle, Edit2, Save, X, ChevronUp, ChevronDown, Eye, EyeOff, 
  RotateCcw, Clock, User, Loader2, GripVertical, AlertCircle, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../hooks/use-toast';
import { format, formatDistanceToNow } from 'date-fns';

const FAQManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ question: '', answer: '' });
  const [activeTab, setActiveTab] = useState('faqs');
  const [rollbackDialog, setRollbackDialog] = useState(null);
  
  const { token } = useAuth();
  const { isDark } = useTheme();
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchFAQs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/faq/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch FAQs');
      const data = await response.json();
      setFaqs(data.faqs || []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load FAQs'
      });
    }
  }, [API_URL, token, toast]);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/faq/logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch logs');
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  }, [API_URL, token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFAQs(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFAQs, fetchLogs]);

  const handleEdit = (faq) => {
    setEditingId(faq.id);
    setEditForm({ question: faq.question, answer: faq.answer });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ question: '', answer: '' });
  };

  const handleSaveEdit = async (faqId) => {
    setSaving(faqId);
    try {
      const response = await fetch(`${API_URL}/api/faq/${faqId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });
      
      if (!response.ok) throw new Error('Failed to update FAQ');
      
      toast({
        title: 'FAQ Updated',
        description: 'Changes saved successfully'
      });
      
      setEditingId(null);
      await Promise.all([fetchFAQs(), fetchLogs()]);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save changes'
      });
    } finally {
      setSaving(null);
    }
  };

  const handleToggleEnabled = async (faq) => {
    setSaving(faq.id);
    try {
      const response = await fetch(`${API_URL}/api/faq/${faq.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: !faq.enabled })
      });
      
      if (!response.ok) throw new Error('Failed to toggle FAQ');
      
      toast({
        title: faq.enabled ? 'FAQ Disabled' : 'FAQ Enabled',
        description: `"${faq.question.substring(0, 30)}..." is now ${faq.enabled ? 'hidden' : 'visible'}`
      });
      
      await Promise.all([fetchFAQs(), fetchLogs()]);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to toggle FAQ'
      });
    } finally {
      setSaving(null);
    }
  };

  const handleMoveUp = async (faq, index) => {
    if (index === 0) return;
    
    const newFaqs = [...faqs];
    const prevFaq = newFaqs[index - 1];
    
    // Swap orders
    setSaving(faq.id);
    try {
      await fetch(`${API_URL}/api/faq/${faq.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ order: prevFaq.order })
      });
      
      await fetch(`${API_URL}/api/faq/${prevFaq.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ order: faq.order })
      });
      
      await Promise.all([fetchFAQs(), fetchLogs()]);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to reorder FAQ'
      });
    } finally {
      setSaving(null);
    }
  };

  const handleMoveDown = async (faq, index) => {
    if (index === faqs.length - 1) return;
    
    const nextFaq = faqs[index + 1];
    
    setSaving(faq.id);
    try {
      await fetch(`${API_URL}/api/faq/${faq.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ order: nextFaq.order })
      });
      
      await fetch(`${API_URL}/api/faq/${nextFaq.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ order: faq.order })
      });
      
      await Promise.all([fetchFAQs(), fetchLogs()]);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to reorder FAQ'
      });
    } finally {
      setSaving(null);
    }
  };

  const handleRollback = async (logId) => {
    setSaving(logId);
    try {
      const response = await fetch(`${API_URL}/api/faq/rollback/${logId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to rollback');
      
      toast({
        title: 'Rollback Successful',
        description: 'FAQ has been restored to previous version'
      });
      
      setRollbackDialog(null);
      await Promise.all([fetchFAQs(), fetchLogs()]);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to rollback'
      });
    } finally {
      setSaving(null);
    }
  };

  const getActionBadge = (action) => {
    const badges = {
      edit: { color: 'bg-blue-500', label: 'Edit' },
      enable: { color: 'bg-green-500', label: 'Enabled' },
      disable: { color: 'bg-orange-500', label: 'Disabled' },
      reorder: { color: 'bg-purple-500', label: 'Reorder' },
      rollback: { color: 'bg-yellow-500', label: 'Rollback' },
      rollback_reorder: { color: 'bg-yellow-500', label: 'Rollback Order' }
    };
    return badges[action] || { color: 'bg-gray-500', label: action };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <HelpCircle className="w-6 h-6 text-yellow-400" />
            FAQ Management
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Edit, reorder, and manage frequently asked questions
          </p>
        </div>
        <Badge variant="outline" className="text-yellow-400 border-yellow-400/50">
          {faqs.filter(f => f.enabled).length} active / {faqs.length} total
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
          <TabsTrigger value="faqs" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
            <HelpCircle className="w-4 h-4 mr-2" />
            FAQs ({faqs.length})
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
            <Clock className="w-4 h-4 mr-2" />
            Edit History ({logs.length})
          </TabsTrigger>
        </TabsList>

        {/* FAQs Tab */}
        <TabsContent value="faqs" className="mt-4">
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <Card 
                key={faq.id}
                className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} ${
                  !faq.enabled ? 'opacity-60' : ''
                } transition-opacity`}
              >
                <CardContent className="p-4">
                  {editingId === faq.id ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Question
                        </label>
                        <Input
                          value={editForm.question}
                          onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                          className={`mt-1 ${isDark ? 'bg-gray-900 border-gray-600 text-white' : ''}`}
                        />
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Answer
                        </label>
                        <Textarea
                          value={editForm.answer}
                          onChange={(e) => setEditForm({ ...editForm, answer: e.target.value })}
                          rows={4}
                          className={`mt-1 ${isDark ? 'bg-gray-900 border-gray-600 text-white' : ''}`}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCancelEdit} size="sm">
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                        <Button 
                          onClick={() => handleSaveEdit(faq.id)}
                          disabled={saving === faq.id}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                        >
                          {saving === faq.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-1" />
                          )}
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex items-start gap-4">
                      {/* Drag Handle & Order */}
                      <div className="flex flex-col items-center gap-1 pt-1">
                        <GripVertical className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                        <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          #{faq.order}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {faq.question}
                          </h4>
                          {!faq.enabled && (
                            <Badge variant="outline" className="text-orange-400 border-orange-400/50 text-xs">
                              Hidden
                            </Badge>
                          )}
                        </div>
                        <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {faq.answer}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Reorder */}
                        <div className="flex flex-col">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveUp(faq, index)}
                            disabled={index === 0 || saving}
                            className="h-7 px-2"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDown(faq, index)}
                            disabled={index === faqs.length - 1 || saving}
                            className="h-7 px-2"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Toggle Visibility */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleEnabled(faq)}
                          disabled={saving === faq.id}
                          className={faq.enabled ? 'text-green-400' : 'text-orange-400'}
                        >
                          {saving === faq.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : faq.enabled ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </Button>

                        {/* Edit */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(faq)}
                          disabled={saving}
                          className="text-blue-400"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Edit History Tab */}
        <TabsContent value="logs" className="mt-4">
          {logs.length === 0 ? (
            <Card className={isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}>
              <CardContent className="py-12 text-center">
                <Clock className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                  No edit history yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const actionBadge = getActionBadge(log.action);
                
                return (
                  <Card 
                    key={log.id}
                    className={isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={`${actionBadge.color} text-white text-xs`}>
                              {actionBadge.label}
                            </Badge>
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              by {log.admin_username}
                            </span>
                          </div>
                          
                          <p className={`text-sm mt-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {log.action === 'edit' && (
                              <>Changed question: "{log.old_data?.question?.substring(0, 50)}..."</>
                            )}
                            {log.action === 'enable' && (
                              <>Enabled: "{log.new_data?.question?.substring(0, 50)}..."</>
                            )}
                            {log.action === 'disable' && (
                              <>Disabled: "{log.old_data?.question?.substring(0, 50)}..."</>
                            )}
                            {log.action === 'reorder' && 'Reordered FAQs'}
                            {log.action.includes('rollback') && 'Rolled back to previous version'}
                          </p>
                          
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                          </p>
                        </div>
                        
                        {/* Rollback Button */}
                        {!log.action.includes('rollback') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setRollbackDialog(log)}
                            className="text-yellow-400 border-yellow-400/50 hover:bg-yellow-400/10"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Rollback
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={!!rollbackDialog} onOpenChange={() => setRollbackDialog(null)}>
        <DialogContent className={isDark ? 'bg-gray-900 border-gray-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              Confirm Rollback
            </DialogTitle>
          </DialogHeader>
          
          <div className={`py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <p>Are you sure you want to rollback this change?</p>
            <p className="text-sm mt-2 text-yellow-400">
              This will restore the FAQ to its previous state.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => rollbackDialog && handleRollback(rollbackDialog.id)}
              disabled={saving}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-1" />
              )}
              Confirm Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FAQManagement;
